/**
 * Firebase web client — env only (TA-027). No hardcoded apiKey / project fallbacks.
 */
import { initializeApp, getApps } from "firebase/app";
import { getAuth } from "firebase/auth";
import { publicEnv } from "@/config/env";

const CONFIG_ERROR =
  "Firebase is not configured. Set NEXT_PUBLIC_FIREBASE_API_KEY, NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN, NEXT_PUBLIC_FIREBASE_PROJECT_ID, and NEXT_PUBLIC_FIREBASE_APP_ID.";

function readFirebaseConfig() {
  const apiKey = publicEnv("NEXT_PUBLIC_FIREBASE_API_KEY");
  const authDomain = publicEnv("NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN");
  const projectId = publicEnv("NEXT_PUBLIC_FIREBASE_PROJECT_ID");
  const appId = publicEnv("NEXT_PUBLIC_FIREBASE_APP_ID");

  if (!apiKey || !authDomain || !projectId || !appId) {
    return null;
  }

  const config = {
    apiKey,
    authDomain,
    projectId,
    appId,
  };

  const storageBucket = publicEnv("NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET");
  const messagingSenderId = publicEnv("NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID");
  const measurementId = publicEnv("NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID");
  if (storageBucket) config.storageBucket = storageBucket;
  if (messagingSenderId) config.messagingSenderId = messagingSenderId;
  if (measurementId) config.measurementId = measurementId;

  return config;
}

export function isFirebaseClientConfigured() {
  return readFirebaseConfig() != null;
}

let authInstance = null;
let initError = null;

export function getFirebaseAuth() {
  if (authInstance) return authInstance;
  if (initError) throw initError;

  const config = readFirebaseConfig();
  if (!config) {
    initError = new Error(CONFIG_ERROR);
    throw initError;
  }

  const app = getApps().length ? getApps()[0] : initializeApp(config);
  authInstance = getAuth(app);
  return authInstance;
}

/**
 * Lazy Auth proxy so existing `import { firebaseAuth }` call sites keep working
 * when env is set; throws a clear error when env is missing (never uses hardcoded keys).
 */
export const firebaseAuth = new Proxy(
  {},
  {
    get(_target, prop) {
      const auth = getFirebaseAuth();
      const value = auth[prop];
      return typeof value === "function" ? value.bind(auth) : value;
    },
  },
);
